import { DateTime } from 'luxon';

export const DATE_FORMAT_PRIMARY = 'MMM d yyyy';
export const TIME_FORMAT_12H = 'h:mm a';

const toUpperAmPm = (value) => (
  typeof value === 'string'
    ? value.replace(/\b(am|pm)\b/gi, (m) => m.toUpperCase())
    : value
);

const addOrdinalSuffix = (day) => {
  if (day > 3 && day < 21) return `${day}th`;

  switch (day % 10) {
    case 1: return `${day}st`;
    case 2: return `${day}nd`;
    case 3: return `${day}rd`;
    default: return `${day}th`;
  }
};

const ensureDateTime = (value) => {
  if (!value) return null;

  if (DateTime.isDateTime(value)) return value;

  if (value instanceof Date) {
    const dt = DateTime.fromJSDate(value);
    return dt.isValid ? dt : null;
  }

  if (typeof value === 'number') {
    const dt = DateTime.fromMillis(value);
    return dt.isValid ? dt : null;
  }

  if (typeof value === 'string') {
    const iso = DateTime.fromISO(value);
    if (iso.isValid) return iso;

    const sql = DateTime.fromSQL(value);
    if (sql.isValid) return sql;
  }

  return null;
};

const formatDateCore = (dateTime) => {
  if (!dateTime?.isValid) return '';

  return `${dateTime.toFormat('MMM')} ${addOrdinalSuffix(dateTime.day)} ${dateTime.year}`;
};

export const formatDate = (value) => {
  const dt = ensureDateTime(value);
  return formatDateCore(dt);
};

export const formatDateTime = (value) => {
  const dt = ensureDateTime(value);
  if (!dt?.isValid) return '';

  return `${formatDateCore(dt)}, ${toUpperAmPm(dt.toFormat(TIME_FORMAT_12H))}`;
};

export const formatTime = (value) => {
  const dt = ensureDateTime(value);
  return dt?.isValid ? toUpperAmPm(dt.toFormat(TIME_FORMAT_12H)) : '';
};

/**
 * The last day an appointment actually occupies. A scheduled interval is half-open, so an end landing
 * exactly on midnight belongs to the day before it: 9am Monday to midnight Tuesday is a Monday
 * appointment. This has to match the rule the office app and the backend use (`lastOccupiedDay` in
 * `components/calendar/utils/multi-day`, `ScheduleSpanPolicy.LastOccupiedLocalDay`), or the portal will
 * tell a customer their job runs a day longer than the crew was scheduled for.
 *
 * The frame is the **customer's own zone**, which is the portal's deliberate convention for everything
 * except booking slots (see the note on `getAvailableSlots` in `api/index.js`). So the closing day here is
 * the last day the appointment occupies *for the reader*, which for a customer far from the company can be
 * a day later than the office app shows. That is the intended reading, not a rounding error.
 */
const lastOccupiedDay = (endDateTime) => {
  const dayStart = endDateTime.startOf('day');

  return endDateTime.equals(dayStart) ? dayStart.minus({ days: 1 }) : dayStart;
};

/** True when the appointment covers more than one calendar day. */
export const spansMultipleDays = (start, end) => {
  const from = ensureDateTime(start);
  const to = ensureDateTime(end);

  if (!from?.isValid || !to?.isValid || to <= from) return false;

  return lastOccupiedDay(to) > from.startOf('day');
};

/**
 * A scheduled interval as one string. Multi-day appointments have to name both ends — a job running
 * Tuesday to Thursday read as a bare "Feb 3rd" gave the customer no way to know the crew would still be
 * on site two days later.
 *
 * `isAllDay` ("Anytime") suppresses the clock times entirely. An Anytime record still stores real times —
 * the anytime window's own bounds, 5:00 AM to 10:00 PM — and printing those as a start and an end reads
 * to a customer as an arrival window nobody promised. Dates only in that case, closed on the last day the
 * work actually occupies so a midnight-ending range does not claim an extra day.
 */
export const formatDateTimeSpan = (start, end, isAllDay = false) => {
  const from = ensureDateTime(start);
  const to = ensureDateTime(end);

  if (!from?.isValid) return '';

  if (isAllDay) {
    if (!to?.isValid || to <= from) return `${formatDate(from)} (Anytime)`;

    return spansMultipleDays(from, to)
      ? `${formatDate(from)} - ${formatDate(lastOccupiedDay(to))} (Anytime)`
      : `${formatDate(from)} (Anytime)`;
  }

  if (!to?.isValid || to <= from) return formatDateTime(from);

  if (spansMultipleDays(from, to)) {
    // The real end instant, not the last occupied day: the time is shown here, so a midnight end needs
    // no reinterpreting and "Aug 27th, 12:00 AM" is both true and unambiguous. This is deliberately a
    // different choice from formatDateSpan above, which is date-only and therefore closes on the last
    // occupied day - the two disagree by a day for a midnight-ending job, and that is why.
    return `${formatDateTime(from)} - ${formatDateTime(to)}`;
  }

  // Within one day the date is not worth repeating, so only the closing time is added.
  return `${formatDateTime(from)} - ${toUpperAmPm(to.toFormat(TIME_FORMAT_12H))}`;
};

/** Dates only, for the compact list rows where the time sits on its own line. */
export const formatDateSpan = (start, end) => {
  const from = ensureDateTime(start);
  const to = ensureDateTime(end);

  if (!from?.isValid) return '';

  return spansMultipleDays(from, to)
    ? `${formatDate(from)} - ${formatDate(lastOccupiedDay(to))}`
    : formatDate(from);
};

export const weekdays = {
  sunday: {
    value: 0,
    label: 'SUN',
  },
  monday: {
    value: 1,
    label: 'MON',
  },
  tuesday: {
    value: 2,
    label: 'TUE',
  },
  wednesday: {
    value: 3,
    label: 'WED',
  },
  thursday: {
    value: 4,
    label: 'THU',
  },
  friday: {
    value: 5,
    label: 'FRI',
  },
  saturday: {
    value: 6,
    label: 'SAT',
  },
};

export const getDate = (value) => {
  const date = value ? new Date(value) : new Date();
  return new Date(date.setHours(0, 0, 0, 0));
};
